import type { FeedbackObject, OverviewSection, SlackText } from "@/components/types";
import type { User } from "firebase/auth";

export interface CreateFeedbackObjectProps {
  user: User;
  userQuestion: string;
  overviewSection: OverviewSection;
  feedbackType: "like" | "dislike";
  feedbackText?: string;
  conversationId?: string;
}
const formatOverviewToMarkdown = (overviewSection: OverviewSection): string => {
  return overviewSection.items
    ?.map((item) => {
      switch (item.type) {
        case "title":
          return `*${item.text}*`;
        case "subtitle":
          return `_${item.text}_`;
        case "paragraph":
          return item.text;
        case "unordered_list":
          return `${item.text}\n${item.items?.map((listItem) => `• ${listItem.text}`).join("\n")}`;
        case "ordered_list":
          return `${item.content}\n${item.items?.map((listItem, index) => `${index + 1}. ${listItem.text}`).join("\n")}`;
        default:
          return (item as SlackText).text || "";
      }
    })
    .join("\n\n");
};

export const createSlackBlocks = ({
  user,
  userQuestion,
  overviewSection,
  feedbackType,
  feedbackText = "",
  conversationId = "",
}: CreateFeedbackObjectProps): FeedbackObject => {
  const timestamp = new Date().toLocaleString("he-IL", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  return {
    blocks: [
      {
        type: "header",
        text: {
          type: "plain_text",
          text: `${feedbackType === "like" ? "✅ Positive" : "❌ Negative"} Feedback Received`,
          emoji: true,
        },
      },
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*📅 Timestamp:* ${timestamp}\n*🎯 Feedback Type:* ${
            feedbackType === "like" ? "👍 Useful" : "👎 Needs Improvement"
          }`,
        },
      },

      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*👤 User:* ${user?.displayName || "Unknown"}\n*🆔 Conversation ID:* \`${conversationId}\``,
        },
      },
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*❓ User Query:*\n\`\`\`\n${userQuestion}\n\`\`\``,
        },
      },
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*📋 Full Overview:*\n\`\`\`\n${formatOverviewToMarkdown(overviewSection)}\n\`\`\``,
        },
      },
      ...(feedbackText
        ? [
            {
              type: "section" as const,
              text: {
                type: "mrkdwn" as const,
                text: `*💬 User Feedback:*\n\`\`\`\n${feedbackText}\n\`\`\``,
              },
            },
          ]
        : []),
      {
        type: "section",
        text: {
          type: "mrkdwn",
          text: `*🔗 Action Required:* ${
            feedbackType === "like"
              ? "Consider this successful interaction for training data"
              : "Review and improve response quality"
          }`,
        },
      },
      { type: "divider" },
    ],
  };
};
